import { ENTITY_COLORS } from '../../appearance/Colors';
import {t as lnT, registerTranslationRefresh} from '../../i18n';
import {useTranslation} from 'react-i18next';
import React from 'react';
import styled from 'styled-components';
import {
    FaBagShopping,
    FaBookOpen,
    FaBuilding,
    FaCalendarDays,
    FaDumbbell,
    FaFilm,
    FaGamepad,
    FaGraduationCap,
    FaLocationDot,
    FaMicrophone,
    FaMobileScreenButton,
    FaMusic,
    FaNewspaper,
    FaPerson,
    FaTags,
    FaTv,
    FaUtensils
} from 'react-icons/fa6';

const makeENTITY_LABELS = () => ({
    book: lnT('common:book'),
    movie: lnT('common:movie'),
    film: lnT('common:film'),
    repair: lnT('common:tutorial'),
    tv_show: lnT('common:tVShow'),
    article: lnT('messages:article'),
    product: lnT('common:product'),
    event: lnT('common:event'),
    workout: lnT('common:workout'),
    person: lnT('common:person'),
    company: lnT('common:company'),
    recipe: lnT('common:recipe'),
    place: lnT('common:place'),
    app: lnT('common:app'),
    game: lnT('common:game'),
    music: lnT('common:music'),
    podcast: lnT('common:podcast'),
    tutorial: lnT('common:tutorial'),
    checklist: lnT('common:checklist'),
    packing_list: lnT('common:packinglist'),
    shopping_list: lnT('common:shoppinglist'),
    procedure: lnT('common:guide'),
    other: lnT('common:other'),
});
export let ENTITY_LABELS = makeENTITY_LABELS();
registerTranslationRefresh(() => {ENTITY_LABELS = makeENTITY_LABELS();});

const makeENTITY_PLURAL_LABELS = () => ({
    book: lnT('common:books'),
    movie: lnT('common:movies'),
    film: lnT('common:films'),
    repair: lnT('common:tutorials'),
    tv_show: lnT('common:tvShows'),
    article: lnT('common:articles'),
    product: lnT('common:products'),
    event: lnT('common:events'),
    workout: lnT('common:workouts'),
    person: lnT('common:people'),
    company: lnT('common:companies'),
    recipe: lnT('common:recipes'),
    place: lnT('common:places'),
    app: lnT('common:apps'),
    game: lnT('common:games'),
    music: lnT('common:music'),
    podcast: lnT('common:podcasts'),
    tutorial: lnT('common:tutorials'),
    checklist: lnT('common:checklists'),
    packing_list: lnT('common:packinglists'),
    shopping_list: lnT('common:shoppinglists'),
    procedure: lnT('common:guides'),
    other: lnT('common:others'),
});
export let ENTITY_PLURAL_LABELS = makeENTITY_PLURAL_LABELS();
registerTranslationRefresh(() => {ENTITY_PLURAL_LABELS = makeENTITY_PLURAL_LABELS();});



export const isMentionEntity = entity => Boolean(entity?.name && entity?.type && entity.type !== 'other');

export const ENTITY_ICONS = {
    book: FaBookOpen,
    movie: FaFilm,
    tv_show: FaTv,
    article: FaNewspaper,
    product: FaBagShopping,
    event: FaCalendarDays,
    workout: FaDumbbell,
    person: FaPerson,
    company: FaBuilding,
    recipe: FaUtensils,
    place: FaLocationDot,
    app: FaMobileScreenButton,
    game: FaGamepad,
    music: FaMusic,
    podcast: FaMicrophone,
    tutorial: FaGraduationCap,
    other: FaTags
};

const Container = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 5px;
`;

const Badge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 7px 3px;
    border-radius: 6px;
    background: ${({ $colors }) => $colors.bg};
    border: 1px solid ${({ $colors }) => $colors.border};
    max-width: 180px;
    min-width: 0;
`;

const BadgeIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    color: ${({ $colors }) => $colors.text};
    opacity: 0.85;
`;

const BadgeText = styled.span`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const EntityName = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${({ $colors }) => $colors.text};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
`;

const EntityType = styled.span`
    font-size: 10px;
    color: ${({ $colors }) => $colors.text};
    opacity: 0.7;
    line-height: 1.2;
    text-transform: capitalize;
`;

const CompactChip = styled.button`
    display: inline-flex;
    align-items: center;
    appearance: none;
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.4;
    background: ${({ $colors }) => $colors.bg};
    border: 1px solid ${({ $colors }) => $colors.border};
    color: ${({ $colors }) => $colors.text};
    white-space: nowrap;
    cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};

    &:hover {
        filter: ${({ $interactive }) => ($interactive ? 'brightness(1.14)' : 'none')};
    }

    &:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 2px;
    }
`;

const CompactContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    align-items: center;
`;

const EntityBadges = ({
    entities,
    maxToShow = 3,
    compact = false,
    showStackIcon = false,
    onEntityClick
}) => {
    const {t: lnT} = useTranslation();

    if (!Array.isArray(entities) || entities.length === 0) {
        return null;
    }

    if (compact) {
        const groupedEntities = entities.reduce((groups, entity) => {
            const type = entity?.type || 'other';
            const group = groups.get(type);
            if (group) {
                group.entities.push(entity);
            } else {
                groups.set(type, { representative: entity, entities: [entity] });
            }
            return groups;
        }, new Map());

        const chips = Array.from(groupedEntities.entries())
            .slice(0, maxToShow)
            .map(([type, group]) => {
                const colors = ENTITY_COLORS[type] || ENTITY_COLORS.other;
                const label = ENTITY_LABELS[type] || lnT('common:other');
                const pluralLabel = ENTITY_PLURAL_LABELS[type] || lnT('common:others');
                const chipLabel =
                    group.entities.length === 1 ? label : lnT('messages:text_a9e9f2e4', { value1: String(group.entities.length), value2: String(pluralLabel) });
                const names = group.entities.map(entity => entity?.name).filter(Boolean);
                const title = names.length > 0 ? names.join(', ') : chipLabel;
                const activateEntity = event => {
                    if (!onEntityClick) return;
                    event.preventDefault();
                    event.stopPropagation();
                    onEntityClick(group.representative, event, {
                        type,
                        entities: group.entities,
                        count: group.entities.length
                    });
                };
                return (
                    <CompactChip
                        key={type}
                        type="button"
                        $colors={colors}
                        $interactive={Boolean(onEntityClick)}
                        title={title}
                        role={onEntityClick ? 'button' : undefined}
                        tabIndex={onEntityClick ? 0 : undefined}
                        data-message-interactive={onEntityClick ? 'true' : undefined}
                        onClick={activateEntity}
                        onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                activateEntity(event);
                            }
                        }}
                    >
                        {chipLabel}
                    </CompactChip>
                );
            });

        if (!chips.length) return null;
        return <CompactContainer>{chips}</CompactContainer>;
    }

    const visible = entities.slice(0, maxToShow);

    return (
        <Container>
            {visible.map((entity, index) => {
                const type = entity?.type || lnT('messages:other');
                const name = entity?.name;
                if (!name) return null;
                const colors = ENTITY_COLORS[type] || ENTITY_COLORS.other;
                const label = ENTITY_LABELS[type] || lnT('common:other');
                const EntityIcon = ENTITY_ICONS[type] || ENTITY_ICONS.other;
                return (
                    <Badge key={index} $colors={colors} title={name}>
                        {showStackIcon ? (
                            <BadgeIcon $colors={colors} aria-hidden="true">
                                <EntityIcon size={12} />
                            </BadgeIcon>
                        ) : null}
                        <BadgeText>
                            <EntityName $colors={colors}>{name}</EntityName>
                            <EntityType $colors={colors}>{label}</EntityType>
                        </BadgeText>
                    </Badge>
                );
            })}
        </Container>
    );
};

export default EntityBadges;
